import { Container, Graphics, SCALE_MODES, Sprite, Texture } from "pixi.js"
import { TileSize, WindLeavesConfig } from "game-common/models"
import { ScalingParams } from "../../../../client_util"
import { daylightFraction } from "./day_gate"

// One airborne leaf. Every field is a plain number so the whole field can be advanced without
// allocating: the update loop mutates these in place and never creates a vector, a point, or an
// options object. 26 of these exist for the life of the session.
interface Leaf {
    sprite: Sprite
    // World position (the same space entity sprites live in -- the leaf container is a child of the
    // translated world surface, so these are world px, not screen px).
    x: number
    y: number
    // Per-leaf multiplier on the shared wind speed. Without this the whole field moves as one rigid
    // sheet, which reads as a scrolling texture rather than as loose debris.
    speedScale: number
    // Sideways wander ACROSS the wind: amplitude in px/sec, and its own frequency/phase so no two
    // leaves wander together.
    swayAmp: number
    swayFreq: number
    swayPhase: number
    // Tumble rate, radians/sec. Signed, so leaves spin both ways.
    spin: number
    // The "leaf turns edge-on" squash: its own frequency/phase, driving scale.y only.
    flutterFreq: number
    flutterPhase: number
    // Size multiplier on the generated leaf texture, and the opacity this leaf reaches once it is
    // clear of the fade band at the edge of the box.
    scale: number
    baseAlpha: number
}

// The room's config folded together with the defaults and any console overrides, reduced to the
// numbers the per-leaf loop actually wants (a unit heading and its perpendicular, not an angle).
interface ResolvedWind {
    // Unit heading, and the unit perpendicular the cross-wind wander rides on.
    windX: number
    windY: number
    crossX: number
    crossY: number
    speed: number
    gustiness: number
    count: number
    alpha: number
    scaleMin: number
    scaleMax: number
    colors: number[]
    // How much of the field the time of day allows, 0..1. Scales both the leaf count and every
    // leaf's opacity, so the wind thins AND fades as the light goes. Never 0 here -- a factor of 0
    // means the effect is off, and resolve() returns null for that.
    daylight: number
}

/**
 * Windblown leaves: a purely cosmetic ambience of leaves tumbling across the world in gusts.
 *
 * WHAT MAKES IT READ AS WIND. Three things, none of which is the drift itself:
 *   1. A shared gust envelope -- two slow sines summed -- swings every leaf's speed together, so the
 *      field surges and lulls as one. Constant-speed particles read as snowfall, not wind.
 *   2. Each leaf wanders ACROSS the wind on its own sine, so the field frays instead of sliding.
 *   3. Each leaf squashes on its short axis on its own cycle, which is what a leaf flipping edge-on
 *      looks like. This is the cheapest cue and the most convincing one.
 *
 * WHERE IT DRAWS. The container is a child of the WORLD surface, so leaves are in world coordinates
 * and parallax correctly as the camera follows the player -- screen-space particles would slide with
 * the player and instantly give the trick away. Being inside the world surface also means the leaves
 * inherit the visibility mask for free: at night, only the leaves crossing lit ground are visible.
 * Its zIndex sits above every tile and entity (so leaves pass in FRONT of things, like foreground
 * debris) but below the sky surface, so a roof still covers them indoors. Nothing is ever y-sorted
 * per frame: re-sorting the world surface's children 60 times a second to interleave 26 leaves with
 * the entities would cost far more than the effect is worth.
 *
 * WHAT IT COSTS. A fixed pool of sprites sharing three generated textures and one tint each, so the
 * whole field batches; no filters, no render textures, no per-frame allocation, and nothing at all
 * when the room has no wind (the container is hidden and update() returns on a boolean). This matters
 * -- the target is a low-end PC with an integrated GPU.
 *
 * ROOM OPT-IN. Nothing is drawn unless the server says so: a room carries a WindLeavesConfig (from
 * its "windLeaves" property, or set directly on a generated room) and the client is told on every
 * room entry, disabled configs included. Ember camps switch it on.
 *
 * ONLY IN DAYLIGHT (config.dayOnly, on by default). The field thins and fades as the light goes and
 * stops dead once it is dark, then gets up again with the morning. It rides the client's already
 * INTERPOLATED brightness rather than the phase name, so the wind dies over the whole of dusk instead
 * of switching off the instant the phase flips. A room with no day cycle has no brightness to read
 * and is treated as permanent day -- the alternative, reading its fixed `darkness`, would leave a
 * dim cave becalmed for reasons that have nothing to do with the hour.
 *
 * LIVE TUNING, from the browser console -- no reload:
 *   window.windLeaves = true | false      force the effect on in ANY room, or off in a windy one
 *   window.windLeavesCount = 40           leaves in the air (capped at MAX_LEAVES)
 *   window.windLeavesSpeed = 80           baseline px/sec along the heading
 *   window.windLeavesDirection = 90       heading in degrees (0 = east, 90 = south)
 *   window.windLeavesGust = 1             0 = steady breeze, 1 = hard surges
 *   window.windLeavesAlpha = 0.5          peak leaf opacity
 *   window.windLeavesDayOnly = false      blow at any hour, ignoring the room's day cycle
 */
export class WindLeaves {
    readonly container: Container

    // Above every tile and entity (world sprites sort at roughly 100 + row * TileSize), below the
    // sky surface at 1_000_000 so a roof still hides the leaves indoors.
    static readonly Z_INDEX = 900000

    // Hard ceiling on the sprite count whatever a room or the console asks for. The effect is
    // ambience; it does not get to cost a frame.
    static readonly MAX_LEAVES = 64

    // The box the simulation lives in, as a half-extent in world px around the player. The visible
    // world is ScalingParams.viewportX/Y tiles either side of the player, so this is that plus a
    // three-tile margin: leaves are born and die outside the screen, never in view.
    private static readonly HALF_W = (ScalingParams.viewportX + 3) * TileSize
    private static readonly HALF_H = (ScalingParams.viewportY + 3) * TileSize

    // How deep the fade-in/out band at the edge of the box is, in world px. A leaf's opacity ramps
    // with its distance to the nearest edge, so nothing ever pops into or out of existence.
    private static readonly FADE_BAND = 44

    // Defaults for everything a config may leave out.
    private static readonly DEFAULT_DIRECTION_DEG = 22
    private static readonly DEFAULT_COUNT = 10
    private static readonly DEFAULT_SPEED = 46
    private static readonly DEFAULT_GUSTINESS = 0.6
    private static readonly DEFAULT_ALPHA = 0.85
    private static readonly DEFAULT_SCALE_MIN = 0.3
    private static readonly DEFAULT_SCALE_MAX = 0.62
    // Tint multiplies, so these are the colours the (white) leaf art becomes. A leaf samples this list
    // uniformly, so the MIX is simply how many entries each shade gets: five living greens to two
    // turned ones, i.e. mostly green with the odd dry leaf among them.
    private static readonly DEFAULT_COLORS = [0x6f9b3a, 0x568a2e, 0x86ab48, 0x4e7c2a, 0x9cbb55, 0xb9762b, 0xd9a13a]

    // A jump bigger than this between frames is a teleport or a room change, not walking, so the
    // whole field is re-scattered rather than dragged across the map behind the player.
    private static readonly TELEPORT_JUMP = WindLeaves.HALF_W

    private leaves: Leaf[] = []
    private activeCount = 0
    private textures: Texture[] = []
    private config: WindLeavesConfig | null = null
    private running = false
    private lastMs = 0
    private lastCenterX = 0
    private lastCenterY = 0
    private hasCenter = false

    constructor(private makeTextures: () => Texture[]) {
        const container = new Container()
        container.zIndex = WindLeaves.Z_INDEX
        // Nothing in here is ever clicked, and it sits over the whole world -- keep it out of hit
        // testing entirely so it can't swallow a press meant for an entity underneath.
        container.interactiveChildren = false
        container.visible = false
        this.container = container
    }

    /**
     * Advance the field one frame and draw it. `center` is the player's world position: the box
     * follows the player, so the simulation only ever runs over what could be on screen.
     *
     * `brightness` is the client's interpolated daylight (ClientGameLogic.currentBrightness), or
     * undefined for a room with no day cycle -- which counts as full daylight, not as darkness.
     *
     * Safe and cheap to call every frame whether or not the room has wind.
     */
    update = (config: WindLeavesConfig | null, center?: { x: number; y: number }, brightness?: number) => {
        const resolved = this.resolve(config, brightness)
        if (!resolved || !center) {
            this.stop()
            return
        }

        const nowMs = performance.now()
        if (!this.running) {
            // Starting (or restarting after a still room): fill the box immediately so the wind is
            // already blowing on the first visible frame, rather than blowing in from one edge.
            this.running = true
            this.container.visible = true
            this.lastMs = nowMs
            this.ensurePool(resolved.count)
            this.scatter(center.x, center.y, resolved)
            this.lastCenterX = center.x
            this.lastCenterY = center.y
            this.hasCenter = true
            return
        }

        this.ensurePool(resolved.count)

        // A teleport (or a new room) moves the box further in one frame than the box is wide. Dragging
        // the existing leaves along would show the previous room's field sliding into place, so
        // re-seed instead.
        if (
            this.hasCenter &&
            (Math.abs(center.x - this.lastCenterX) > WindLeaves.TELEPORT_JUMP ||
                Math.abs(center.y - this.lastCenterY) > WindLeaves.TELEPORT_JUMP)
        ) {
            this.scatter(center.x, center.y, resolved)
            this.lastMs = nowMs
            this.lastCenterX = center.x
            this.lastCenterY = center.y
            return
        }
        this.lastCenterX = center.x
        this.lastCenterY = center.y
        this.hasCenter = true

        // Time-based, so the wind blows at the same speed on a 30fps laptop and a 144Hz monitor. The
        // clamp is for the tab coming back from the background, where the gap can be minutes: without
        // it every leaf would teleport clean across the map in a single step.
        let dt = (nowMs - this.lastMs) / 1000
        this.lastMs = nowMs
        if (!(dt > 0)) {
            return
        }
        dt = Math.min(dt, 0.05)

        this.step(dt, nowMs / 1000, center.x, center.y, resolved)
    }

    // Exposed for tests: how many leaves are currently simulated and drawn.
    getActiveCount = () => (this.running ? this.activeCount : 0)

    private stop = () => {
        if (!this.running) {
            return
        }
        this.running = false
        this.container.visible = false
        // Next enable must not integrate the whole time the effect was off.
        this.lastMs = 0
        this.hasCenter = false
    }

    /**
     * Fold the room's config, the defaults and the console overrides into the numbers the simulation
     * actually uses -- or null if the effect is off. One small object per frame, which is nothing next
     * to the branchy alternative of reading `window` from inside the per-leaf loop.
     */
    private resolve = (config: WindLeavesConfig | null, brightness?: number): ResolvedWind | null => {
        const w = window as any
        const forced = typeof w.windLeaves === "boolean" ? (w.windLeaves as boolean) : undefined
        const enabled = forced !== undefined ? forced : !!config?.enabled
        if (!enabled) {
            return null
        }

        const daylight = this.daylightFactor(config, brightness)
        if (daylight <= 0) {
            // Night. Returning null takes the same path as a room with no wind: the container is
            // hidden and not one leaf is simulated until the light comes back.
            return null
        }

        const num = (override: any, value: number | undefined, fallback: number) =>
            typeof override === "number" ? override : typeof value === "number" ? value : fallback

        const directionDeg = num(w.windLeavesDirection, config?.directionDeg, WindLeaves.DEFAULT_DIRECTION_DEG)
        const rad = (directionDeg * Math.PI) / 180
        const scaleMin = config?.scaleMin ?? WindLeaves.DEFAULT_SCALE_MIN
        const scaleMax = config?.scaleMax ?? WindLeaves.DEFAULT_SCALE_MAX

        return {
            // Unit heading, and the unit perpendicular the cross-wind wander rides on.
            windX: Math.cos(rad),
            windY: Math.sin(rad),
            crossX: -Math.sin(rad),
            crossY: Math.cos(rad),
            speed: Math.max(1, num(w.windLeavesSpeed, config?.speed, WindLeaves.DEFAULT_SPEED)),
            gustiness: Math.min(1, Math.max(0, num(w.windLeavesGust, config?.gustiness, WindLeaves.DEFAULT_GUSTINESS))),
            // Thinned by the time of day, but never rounded away to nothing while there is still
            // light: a last leaf or two in the failing light is the point of the fade.
            count: Math.max(
                daylight > 0 ? 1 : 0,
                Math.min(
                    WindLeaves.MAX_LEAVES,
                    Math.round(num(w.windLeavesCount, config?.count, WindLeaves.DEFAULT_COUNT) * daylight),
                ),
            ),
            alpha: Math.min(1, Math.max(0, num(w.windLeavesAlpha, config?.alpha, WindLeaves.DEFAULT_ALPHA))),
            scaleMin: Math.max(0.05, Math.min(scaleMin, scaleMax)),
            scaleMax: Math.max(scaleMin, scaleMax),
            colors: config?.colors?.length ? config.colors : WindLeaves.DEFAULT_COLORS,
            daylight,
        }
    }

    /**
     * How much of the field this hour allows, 0..1. The ramp itself (and why it reads the interpolated
     * brightness rather than the phase name) lives in day_gate.ts, shared with the night fog so the
     * two hand over to each other at the same points.
     */
    private daylightFactor = (config: WindLeavesConfig | null, brightness?: number): number => {
        const w = window as any
        const dayOnly = typeof w.windLeavesDayOnly === "boolean" ? w.windLeavesDayOnly : config?.dayOnly !== false
        if (!dayOnly) {
            return 1
        }
        return daylightFraction(brightness)
    }

    private step = (dt: number, tSeconds: number, centerX: number, centerY: number, cfg: ResolvedWind) => {
        // The shared gust envelope: two slow, incommensurate sines, so the surges never fall into an
        // audible-looking rhythm. Floored above zero -- a gust that reaches 0 leaves the field hanging
        // motionless in the air, which looks broken rather than calm.
        const gust = Math.max(
            0.15,
            1 + cfg.gustiness * (0.62 * Math.sin(tSeconds * 0.41) + 0.38 * Math.sin(tSeconds * 1.13 + 1.7)),
        )

        for (let i = 0; i < this.activeCount; i++) {
            const leaf = this.leaves[i]
            const along = cfg.speed * gust * leaf.speedScale
            const cross = Math.sin(tSeconds * leaf.swayFreq + leaf.swayPhase) * leaf.swayAmp

            leaf.x += (cfg.windX * along + cfg.crossX * cross) * dt
            leaf.y += (cfg.windY * along + cfg.crossY * cross) * dt

            // Distance to the nearest edge of the box, which is both the recycle test and the fade.
            const edgeX = WindLeaves.HALF_W - Math.abs(leaf.x - centerX)
            const edgeY = WindLeaves.HALF_H - Math.abs(leaf.y - centerY)
            if (edgeX <= 0 || edgeY <= 0) {
                this.reseed(leaf, centerX, centerY, cfg)
                continue
            }

            const sprite = leaf.sprite
            sprite.x = leaf.x
            sprite.y = leaf.y
            // Tumble. Faster in a gust, because a leaf that spins at a constant rate while its drift
            // surges looks like it is on rails.
            sprite.rotation += leaf.spin * gust * dt
            // Edge-on flip: squash the short axis only. abs() of a cosine gives the sharp turn at the
            // moment the leaf passes through edge-on, which a plain sine would smooth away.
            const flutter = Math.abs(Math.cos(tSeconds * leaf.flutterFreq + leaf.flutterPhase))
            sprite.scale.set(leaf.scale, leaf.scale * (0.3 + 0.7 * flutter))
            // cfg.daylight is applied HERE and not folded into baseAlpha at reseed: a leaf keeps its
            // baseAlpha for its whole crossing, so baking the light in would leave the field fading
            // one leaf at a time as each got recycled instead of dimming together.
            sprite.alpha = leaf.baseAlpha * cfg.daylight * Math.min(1, Math.min(edgeX, edgeY) / WindLeaves.FADE_BAND)
        }
    }

    /**
     * Grow the sprite pool to `count` (once; sprites are never destroyed) and hide any surplus if the
     * count was turned down. Leaves keep their simulation state while hidden, so turning the density
     * back up does not restart the field.
     */
    private ensurePool = (count: number) => {
        const target = Math.min(count, WindLeaves.MAX_LEAVES)
        if (!this.textures.length && target > 0) {
            // A leaf is ambience. If the bake fails (no GPU -- a test, a lost context), draw plain
            // tinted quads rather than taking the frame down.
            try {
                this.textures = this.makeTextures()
            } catch (e) {
                this.textures = []
            }
            if (!this.textures.length) {
                this.textures = [Texture.WHITE]
            }
        }
        while (this.leaves.length < target) {
            const sprite = new Sprite(this.textures[this.leaves.length % this.textures.length] ?? Texture.WHITE)
            sprite.anchor.set(0.5)
            this.container.addChild(sprite)
            this.leaves.push({
                sprite,
                x: 0,
                y: 0,
                speedScale: 1,
                swayAmp: 0,
                swayFreq: 1,
                swayPhase: 0,
                spin: 0,
                flutterFreq: 1,
                flutterPhase: 0,
                scale: 0.5,
                baseAlpha: 1,
            })
        }
        if (target === this.activeCount) {
            return
        }
        for (let i = 0; i < this.leaves.length; i++) {
            this.leaves[i].sprite.visible = i < target
        }
        this.activeCount = target
    }

    // Fill the whole box: used on the first enabled frame and after a teleport.
    private scatter = (centerX: number, centerY: number, cfg: ResolvedWind) => {
        for (let i = 0; i < this.activeCount; i++) {
            const leaf = this.leaves[i]
            this.randomizeLeaf(leaf, cfg)
            leaf.x = centerX + (Math.random() * 2 - 1) * WindLeaves.HALF_W
            leaf.y = centerY + (Math.random() * 2 - 1) * WindLeaves.HALF_H
            leaf.sprite.x = leaf.x
            leaf.sprite.y = leaf.y
            leaf.sprite.alpha = 0
        }
    }

    /**
     * Send a leaf that has left the box back in on the UPWIND edge, with fresh flight characteristics
     * so the field never settles into a repeating pattern. Which of the two upwind edges it enters
     * through is weighted by the wind heading, so a diagonal wind feeds leaves in from both.
     */
    private reseed = (leaf: Leaf, centerX: number, centerY: number, cfg: ResolvedWind) => {
        this.randomizeLeaf(leaf, cfg)

        const absX = Math.abs(cfg.windX)
        const absY = Math.abs(cfg.windY)
        // Weighted pick with no zero-division: with a purely vertical wind absX is 0 and this never
        // takes the horizontal arm, which is what we want (its sign would be 0 and the leaf would be
        // born in the middle of the screen).
        if (Math.random() * (absX + absY) < absX) {
            leaf.x = centerX - Math.sign(cfg.windX) * WindLeaves.HALF_W
            leaf.y = centerY + (Math.random() * 2 - 1) * WindLeaves.HALF_H
        } else {
            leaf.x = centerX + (Math.random() * 2 - 1) * WindLeaves.HALF_W
            leaf.y = centerY - Math.sign(cfg.windY) * WindLeaves.HALF_H
        }
        leaf.sprite.x = leaf.x
        leaf.sprite.y = leaf.y
        leaf.sprite.alpha = 0
    }

    private randomizeLeaf = (leaf: Leaf, cfg: ResolvedWind) => {
        const rand = (min: number, max: number) => min + Math.random() * (max - min)
        leaf.speedScale = rand(0.62, 1.45)
        // Sway amplitude is a fraction of the drift speed: a slow leaf that wanders as hard as a fast
        // one looks like it is being steered rather than carried.
        leaf.swayAmp = cfg.speed * rand(0.18, 0.5)
        leaf.swayFreq = rand(0.5, 1.9)
        leaf.swayPhase = Math.random() * Math.PI * 2
        leaf.spin = rand(0.6, 3.1) * (Math.random() < 0.5 ? -1 : 1)
        leaf.flutterFreq = rand(1.4, 4.2)
        leaf.flutterPhase = Math.random() * Math.PI * 2
        leaf.scale = rand(cfg.scaleMin, cfg.scaleMax)
        // Smaller leaves read as further away, so they are also fainter. Cheap depth for free.
        const depth = (leaf.scale - cfg.scaleMin) / Math.max(0.0001, cfg.scaleMax - cfg.scaleMin)
        leaf.baseAlpha = cfg.alpha * (0.6 + 0.4 * depth)
        leaf.sprite.texture = this.textures[Math.floor(Math.random() * this.textures.length)] ?? Texture.WHITE
        leaf.sprite.tint = cfg.colors[Math.floor(Math.random() * cfg.colors.length)]
    }

    /**
     * The leaf art, generated once at startup rather than shipped as an asset: three white blades with
     * a grey midrib, which every leaf then tints. White art is what makes a single texture serve a
     * whole seasonal palette (tint multiplies), and the grey rib survives tinting as a darker vein.
     *
     * Drawn oversized and scaled down per leaf, at resolution 2 to match the world surface's 2x zoom,
     * so the edges stay smooth instead of showing the blocky diagonal of a 6px sprite.
     */
    static buildLeafTextures = (generate: (g: Graphics) => Texture): Texture[] => {
        // Long/narrow, short/round, and a thin sliver: enough variety that the eye stops reading a
        // repeated stamp, few enough that all three stay in one batch.
        const shapes: Array<[number, number]> = [
            [17, 7],
            [13, 10],
            [15, 4],
        ]
        return shapes.map(([length, width]) => {
            const g = new Graphics()
            g.beginFill(0xffffff)
            // A lens: two quadratic curves from tip to tip. The control points at ±width sit outside
            // the curve, so the widest point of the blade lands near width/2 -- deliberate, it gives
            // the leaf a pointed tip rather than an ellipse's blunt one.
            g.moveTo(-length / 2, 0)
            g.quadraticCurveTo(0, -width, length / 2, 0)
            g.quadraticCurveTo(0, width, -length / 2, 0)
            g.endFill()
            // Midrib, stopping short of both tips so it doesn't spill outside the blade.
            g.lineStyle(1, 0x9a9a9a, 0.85)
            g.moveTo(-length / 2 + 1.5, 0)
            g.lineTo(length / 2 - 1.5, 0)
            const texture = generate(g)
            g.destroy()
            return texture
        })
    }

    // The default texture factory, bound to a live pixi renderer. Split from buildLeafTextures so the
    // simulation can be tested without a GPU.
    static textureFactory = (renderer: { generateTexture: (g: Graphics, options?: any) => Texture }) => (): Texture[] =>
        WindLeaves.buildLeafTextures(g => renderer.generateTexture(g, { resolution: 2, scaleMode: SCALE_MODES.LINEAR }))
}
